import { ApolloError } from "@apollo/client";
import {
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { Controller, useForm } from "react-hook-form";

import { Alert, Button } from "../../../components";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import useCurrentUser from "../../hooks/useCurrentUser";
import { PositionSelect, StageSelect } from "..";
import UserSelect from "../UserSelect";

export interface FormData {
  name?: string;
  positionId?: string;
  interviewStageId?: string;
  assignedUserIds?: string[];
}

interface CallGuideFormProps {
  name?: string;
  positionId?: string;
  interviewStageId?: string;
  assignedUserIds?: string[];
  error: ApolloError | undefined;
  isLoading: boolean;
  onSubmit: (formData: FormData) => void;
  onCancel: () => void;
  title?: string;
  guideCount?: number;
}

const GuideForm: React.FC<CallGuideFormProps> = ({
  name,
  positionId,
  interviewStageId,
  assignedUserIds,
  error,
  isLoading,
  onSubmit,
  onCancel,
  title,
  guideCount,
}) => {
  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      name,
      positionId,
      interviewStageId,
      assignedUserIds,
    },
  });

  const submit = handleSubmit(
    ({ name, positionId, interviewStageId, assignedUserIds }): void => {
      onSubmit({
        name: name?.trim(),
        positionId,
        interviewStageId,
        assignedUserIds,
      });
    }
  );

  const currentUser = useCurrentUser();
  const canAssignGuides =
    currentUser.organization.assignGuidesToInterviewersEnabled &&
    currentUser.userRole?.canManageHiringTeam;
  const crowdstrikeDemo = useFeatureFlag("crowdstrike-demo");
  const guidesDemoEnabled = useFeatureFlag("guidesDemoEnabled");
  const demosEnabled = crowdstrikeDemo || guidesDemoEnabled;

  const guideStageAssignment = useFeatureFlag("guide-stage-assignment");
  const isBulkUpdate = guideCount && guideCount > 1;

  return (
    <form onSubmit={submit}>
      <ModalContent>
        <ModalHeader pb="0">
          {guideCount
            ? `Edit ${guideCount} guide${guideCount === 1 ? "" : "s"}`
            : title}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {error?.graphQLErrors?.map(({ message }, i) => (
            <Alert
              mb={6}
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              status="error"
              description={message || "Error saving guide"}
            />
          ))}
          <VStack spacing="6">
            {!isBulkUpdate && (
              <FormControl isRequired isInvalid={errors.name !== undefined}>
                <FormLabel color="gray.600" htmlFor="name">
                  Name
                </FormLabel>
                <Input
                  {...register("name")}
                  aria-label="name"
                  borderRadius="sm"
                  placeholder="Screen, Interview, Intake, etc."
                  maxLength={100}
                  autoFocus
                />
                {errors.name !== undefined && (
                  <FormErrorMessage>{errors.name.message}</FormErrorMessage>
                )}
              </FormControl>
            )}
            <FormControl>
              <FormLabel color="gray.600" htmlFor="positionId">
                {guideCount
                  ? "Update position for selected guides (optional)"
                  : "Attach to a Position (optional)"}
              </FormLabel>
              <Controller
                name="positionId"
                control={control}
                render={({ field }) => (
                  <PositionSelect
                    {...field}
                    positionId={field.value}
                    onSelect={(position) => {
                      if (position?.id !== field.value) {
                        setValue("positionId", position?.id || "");
                      }
                    }}
                  />
                )}
              />
              {errors.positionId !== undefined && (
                <FormErrorMessage>{errors.positionId.message}</FormErrorMessage>
              )}
            </FormControl>
            {(guideStageAssignment || demosEnabled) && !isBulkUpdate && (
              <FormControl id="stage-ids">
                <FormLabel color="gray.600">
                  {guideCount
                    ? "Update stage for selected guides (optional)"
                    : "Attach a Stage (optional)"}
                </FormLabel>
                <Controller
                  name="interviewStageId"
                  control={control}
                  render={({ field }) => (
                    <StageSelect
                      {...field}
                      interviewStageId={field.value}
                      onSelect={(stage) => {
                        if (stage?.id !== field.value) {
                          setValue("interviewStageId", stage?.id || "");
                        }
                      }}
                    />
                  )}
                />
                {errors.interviewStageId !== undefined && (
                  <FormErrorMessage>
                    {errors.interviewStageId.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            )}
            {(demosEnabled || canAssignGuides) && (
              <FormControl id="interviewer-ids">
                <FormLabel color="gray.600">
                  {guideCount
                    ? "Update assigned interviewers for selected guides (optional)"
                    : "Assign to interviewers (optional)"}
                </FormLabel>
                <Controller
                  name="assignedUserIds"
                  control={control}
                  render={({ field }) => (
                    <UserSelect
                      {...field}
                      userIds={field.value}
                      onSelect={(userIds) =>
                        setValue("assignedUserIds", userIds)
                      }
                    />
                  )}
                />
                {errors.assignedUserIds !== undefined && (
                  <FormErrorMessage>
                    {errors.assignedUserIds.message}
                  </FormErrorMessage>
                )}
              </FormControl>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter borderColor="gray.100 !important" borderTop="1px solid">
          <ButtonGroup
            spacing={4}
            alignSelf="end"
            width="100%"
            justifyContent="space-between"
          >
            <Button fontWeight="500" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              fontWeight="500"
              type="submit"
              isLoading={isLoading}
              borderRadius="lg"
            >
              Save
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </form>
  );
};

export default GuideForm;
